import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ToolSidebar from '../../utils/ToolSideBar';
import { createTeam, fetchTeamCreateOptions, fetchTeams } from '../../../services/teamApi';
import '../../../css/superuser/StaffDirectoryPage.css';
import '../../../css/staff/InternalWorkspace.css';

function isManagerView() {
  const role = localStorage.getItem('portal_role') || 'client';
  const isSuperuser = localStorage.getItem('is_superuser') === 'true';
  return isSuperuser || role === 'hr';
}

function TeamSkeletons() {
  return (
    <div className="team-grid" aria-label="Loading teams">
      {[0, 1, 2].map((item) => <div className="team-card team-card-skeleton" key={item} />)}
    </div>
  );
}

function ValueBlock({ label, value, children }) {
  return (
    <div className="team-value">
      <span>{label}</span>
      <strong>{children || value || 'Not assigned'}</strong>
    </div>
  );
}

function memberSectionLabel(selectedType) {
  const label = selectedType?.member_label || 'Team Members';
  return label.replace(/ developers$/i, ' Developers');
}

function roleLine(member, selectedType) {
  if (member.developer_discipline) {
    return `${member.developer_discipline.replace('_', ' ')} Developer`;
  }
  if (member.role === 'designer') return 'Designer';
  return selectedType?.label ? `${selectedType.label} Team Member` : 'Team Member';
}

function CreateTeamModal({ onClose, onCreated, initialTypes }) {
  const [options, setOptions] = useState({ team_types: initialTypes || [], project_managers: [], members: [] });
  const [teamType, setTeamType] = useState(initialTypes?.[0]?.value || 'frontend');
  const [pmId, setPmId] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedType = useMemo(() => (options.team_types || []).find((item) => item.value === teamType), [options.team_types, teamType]);
  const maxMembers = selectedType?.max_members ?? null;
  const members = options.members || [];
  const canSubmit = Boolean(teamType && pmId && !loading && !saving);
  const sectionLabel = memberSectionLabel(selectedType);

  const loadOptions = useCallback(async (type) => {
    setLoading(true);
    try {
      const data = await fetchTeamCreateOptions(type);
      setOptions(data);
      setTeamType(data.selected_team_type || type || data.team_types?.[0]?.value || 'frontend');
      setSelectedMembers((current) => current.filter((id) => (data.members || []).some((member) => String(member.id) === String(id))));
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load team options.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOptions(teamType); }, [loadOptions, teamType]);

  function toggleMember(memberId) {
    setSelectedMembers((current) => {
      const normalized = String(memberId);
      if (current.includes(normalized)) return current.filter((item) => item !== normalized);
      if (maxMembers !== null && current.length >= maxMembers) {
        setError(`${selectedType?.label || 'This'} team can have at most ${maxMembers} members.`);
        return current;
      }
      setError('');
      return [...current, normalized];
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await createTeam({
        team_type: teamType,
        project_manager_id: Number(pmId),
        initial_member_ids: selectedMembers.map((item) => Number(item)),
      });
      await onCreated?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Unable to create team.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="team-selector-backdrop" role="presentation">
      <section className="staff-card team-selector-modal create-team-modal" role="dialog" aria-modal="true" aria-labelledby="create-team-title">
        <header className="create-team-header">
          <div>
            <span>SCD DELIVERY</span>
            <h2 id="create-team-title">Create Team</h2>
            <p>Build a delivery team by choosing its specialty, project manager, and members.</p>
          </div>
          <button type="button" className="team-modal-close" aria-label="Close modal" onClick={onClose}>{'\u00d7'}</button>
        </header>
        <form className="team-create-form" onSubmit={handleSubmit}>
          <div className="team-create-primary-row">
            <section className="team-create-section create-team-field">
              <label htmlFor="team-type-select">Team Type</label>
              <select id="team-type-select" value={teamType} onChange={(event) => { setTeamType(event.target.value); setSelectedMembers([]); }} required>
                {(options.team_types || initialTypes || []).map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
              </select>
              <div className="create-team-field-help">Team member options are filtered automatically by specialty.</div>
            </section>
            <section className="team-create-section create-team-field">
              <label htmlFor="project-manager-select">Project Manager</label>
              <select id="project-manager-select" value={pmId} onChange={(event) => setPmId(event.target.value)} required disabled={loading}>
                <option value="">Select project manager</option>
                {(options.project_managers || []).map((pm) => <option key={pm.id} value={pm.id}>{pm.name}</option>)}
              </select>
              <div className="create-team-field-help">{!loading && (options.project_managers || []).length === 0 ? 'No active project managers are available yet.' : ''}</div>
            </section>
          </div>

          <section className="team-create-section team-create-members-section">
            <div className="team-member-section-head">
              <label>{sectionLabel}</label>
              {maxMembers !== null && <strong>{selectedMembers.length} / {maxMembers} selected</strong>}
            </div>
            {loading ? <p className="staff-empty inline">Loading members...</p> : members.length === 0 ? (
              <div className="team-member-empty-state">
                <strong>No {sectionLabel} Available</strong>
                <p>You can still create the team with a Project Manager and add developers later.</p>
              </div>
            ) : (
              <div className="team-member-picker">
                {members.map((member) => {
                  const checked = selectedMembers.includes(String(member.id));
                  return (
                    <label className={checked ? 'team-member-choice is-selected' : 'team-member-choice'} key={member.id}>
                      <input type="checkbox" aria-label={member.name} checked={checked} onChange={() => toggleMember(member.id)} />
                      <span>
                        <strong>{member.name}</strong>
                        <small>{roleLine(member, selectedType)}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </section>

          <footer className="team-modal-actions">
            <span>{error || ''}</span>
            <div>
              <button type="button" className="team-secondary-button" onClick={onClose}>Cancel</button>
              <button type="submit" disabled={!canSubmit}>{saving ? 'Creating...' : 'Create Team'}</button>
            </div>
          </footer>
        </form>
      </section>
    </div>
  );
}

function TeamCard({ team }) {
  const project = team.current_project;
  const health = team.project_health;
  const memberCount = team.member_count || 0;
  return (
    <article className="team-card">
      <div className="team-card-head">
        <div>
          <span className="team-type-badge">{team.team_type_label || 'Delivery'}</span>
          <h2>{team.name}</h2>
          <p>{team.is_active ? 'Active team' : 'Inactive team'}</p>
        </div>
        <span className={team.is_active ? 'team-status is-active' : 'team-status'}>{team.is_active ? 'Active' : 'Inactive'}</span>
      </div>
      <div className="team-card-grid">
        <ValueBlock label="Project Manager" value={team.project_manager?.name || 'Unassigned'} />
        <ValueBlock label="Members" value={`${memberCount} ${memberCount === 1 ? 'member' : 'members'}`} />
        <ValueBlock label="Current Project" value={project?.name || 'No active project'} />
        <ValueBlock label="Health">
          {health ? <span className={`health-pill health-${health.key}`}>{health.label}</span> : 'No active project'}
        </ValueBlock>
        <ValueBlock label="Rating" value={team.rating?.display || 'Not rated'} />
      </div>
      <Link className="team-card-action" to={`/team/${team.id}`}>View Team</Link>
    </article>
  );
}

export default function TeamListPage() {
  const [teams, setTeams] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const managerView = useMemo(() => isManagerView(), []);

  const loadTeams = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTeams();
      setTeams(data.results || []);
      setMeta(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load teams.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTeams(); }, [loadTeams]);

  const emptyText = managerView ? 'No teams to display.' : 'You are not currently assigned to manage a team.';
  const canCreateTeam = Boolean(meta.can_create_team || meta.permissions?.can_create_team);

  return (
    <div className="staff-shell">
      <ToolSidebar active="team" />
      <main className="staff-page internal-page team-page">
        <header className="staff-header internal-hero team-list-header">
          <div>
            <span>{meta.eyebrow || 'SCD DELIVERY'}</span>
            <h1>{meta.heading || 'Teams'}</h1>
            <p>{meta.supporting_text || (managerView ? 'Manage delivery teams, assignments, and team performance.' : 'Your delivery teams and current project assignments.')}</p>
          </div>
          {canCreateTeam && <button type="button" className="team-create-button" onClick={() => setShowCreate(true)}>+ Create Team</button>}
        </header>

        <section className="staff-card team-list-card">
          {loading ? <TeamSkeletons /> : error ? <p className="staff-empty inline team-error">{error}</p> : teams.length === 0 ? <p className="staff-empty inline">{emptyText}</p> : (
            <div className="team-grid">
              {teams.map((team) => <TeamCard key={team.id} team={team} />)}
            </div>
          )}
        </section>
      </main>
      {showCreate && <CreateTeamModal initialTypes={meta.team_types} onClose={() => setShowCreate(false)} onCreated={loadTeams} />}
    </div>
  );
}
